import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Loader2, Save, X, Info, UserPlus, UserMinus, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { teamAPI, userAPI, applicationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'it_support', label: 'IT Support' },
  { value: 'it_manager', label: 'IT Manager' },
  { value: 'approver', label: 'Approver' },
  { value: 'admin', label: 'Admin' },
  { value: 'end_user', label: 'End User' },
];

export default function TeamsPage() {
  const { user } = useAuth();
  // IT Support and Approver are read-only on this page
  const isReadOnly = ['it_support', 'approver'].includes(
    (user?.role_name || '').toLowerCase().replace(/\s+/g, '_')
  );
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [addMemberTeam, setAddMemberTeam] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set()); // multi-select
  const [addRole, setAddRole] = useState('member');
  const [memberRoleFilter, setMemberRoleFilter] = useState('it_support');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberUserList, setMemberUserList] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberPage, setMemberPage] = useState(1);
  const [form, setForm] = useState({ name: '', description: '', application: '', distribution_email: '', color: '#3B82F6', round_robin_enabled: false });
  const [selectedApp, setSelectedApp] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const [tRes, appRes] = await Promise.allSettled([teamAPI.list(), applicationAPI.list({ page_size: 500 })]);
      const teamsData = tRes.status === 'fulfilled' ? (tRes.value.data?.results || tRes.value.data || []) : [];
      const detailed = await Promise.all(teamsData.map(async t => {
        try { const d = await teamAPI.get(t.id); return d.data; } catch { return t; }
      }));
      setTeams(detailed);
      if (appRes.status === 'fulfilled') setAllApps(appRes.value.data?.results || appRes.value.data || []);
    } catch { setTeams([]); }
    finally { setLoading(false); }
  };

  const loadMemberUsers = useCallback(async (role = memberRoleFilter, search = memberSearch, page = 1) => {
    setMemberLoading(true);
    try {
      const params = { page_size: 20, page };
      if (role) params.role = role;
      if (search) params.search = search;
      const res = await userAPI.list(params);
      setMemberUserList(res.data?.results || res.data || []);
      setMemberPage(page);
    } catch { setMemberUserList([]); }
    finally { setMemberLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const handleAppChange = (appId) => {
    setForm(prev => ({ ...prev, application: appId }));
    const app = allApps.find(a => a.id === appId);
    setSelectedApp(app || null);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    if (!form.application) { toast.error('Application required'); return; }
    const data = { name: form.name, description: form.description, application: form.application, distribution_email: form.distribution_email, color: form.color, round_robin_enabled: form.round_robin_enabled };
    try {
      if (editing) { await teamAPI.update(editing, data); toast.success('Team updated'); }
      else { await teamAPI.create(data); toast.success('Team created'); }
      setShowForm(false); setEditing(null); setSelectedApp(null); fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleAddMember = async (teamId) => {
    if (selectedMembers.size === 0) { toast.error('Select at least one user'); return; }
    let added = 0, failed = 0;
    for (const userId of selectedMembers) {
      try { await teamAPI.addMember(teamId, { user: userId, role_in_team: addRole }); added++; }
      catch { failed++; }
    }
    if (added > 0) toast.success(`${added} member${added > 1 ? 's' : ''} added successfully`);
    if (failed > 0) toast.error(`${failed} failed to add`);
    setAddMemberTeam(null); setSelectedMembers(new Set()); fetch();
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (!window.confirm('Remove this member?')) return;
    try { await teamAPI.removeMember(teamId, userId); toast.success('Removed'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const startEdit = (team) => {
    setForm({ name: team.name, description: team.description || '', application: team.application || '', distribution_email: team.distribution_email || '', color: team.color || '#3B82F6', round_robin_enabled: team.round_robin_enabled || false });
    const app = allApps.find(a => a.id === team.application);
    setSelectedApp(app || null);
    setEditing(team.id); setShowForm(true);
  };

  const availableUsers = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    const memberIds = new Set((team?.members || []).map(m => m.user));
    return allUsers.filter(u => !memberIds.has(u.id));
  };

  const getAppForTeam = (team) => allApps.find(a => a.id === team.application);

  return (
    <div data-testid="teams-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2"><Users className="w-6 h-6 text-sky-600" /> Apps & Teams Mapping</h2>
          <p className="text-sm text-slate-500 mt-1">Map applications to support teams and assign IT support members</p>
        </div>
        {!isReadOnly && (
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', application: '', distribution_email: '', color: '#3B82F6', round_robin_enabled: false }); setSelectedApp(null); }}
            className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600"><Plus className="w-4 h-4" /> Create Team</button>
        )}
      </div>

      <div className="bg-sky-50 dark:bg-sky-900/20 p-4 rounded-xl border border-sky-200 dark:border-sky-800 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-sky-700 dark:text-sky-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-sky-900 dark:text-sky-300">Select an application to see its approval chain. Add IT support members who will be assigned tickets for this application.</p>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Team</h3>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <input placeholder="Team Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <div>
              <select value={form.application} onChange={e => handleAppChange(e.target.value)} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select Application *</option>
                {allApps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <input type="email" placeholder="Distribution Email (DL)" value={form.distribution_email} onChange={e => setForm({...form, distribution_email: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
          </div>

          {/* Read-only Application Details */}
          {selectedApp && (
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 mb-4 border dark:border-slate-700">
              <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 mb-3">Application Details (from {selectedApp.name})</h4>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block">Business Owner</label>
                  <span className="text-sm font-medium dark:text-white">{selectedApp.owner_name || 'Not set'}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block">Technical Owner</label>
                  <span className="text-sm font-medium dark:text-white">{selectedApp.technical_owner_name || 'Not set'}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block">L1: Team Lead</label>
                  <span className="text-sm font-medium dark:text-white">{selectedApp.app_team_lead_name || 'Not set'}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block">L2: App Manager</label>
                  <span className="text-sm font-medium dark:text-white">{selectedApp.app_manager_name || 'Not set'}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block">L3: App Head</label>
                  <span className="text-sm font-medium dark:text-white">{selectedApp.app_head_name || 'Not set'}</span>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500">Approval: <span className="font-medium text-sky-600">{selectedApp.approval_levels === 0 ? 'None' : `${selectedApp.approval_levels} Levels`}</span></div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); setSelectedApp(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : teams.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No teams configured. Create your first team above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {teams.map(team => {
            const isExpanded = expandedTeam === team.id;
            const members = team.members || [];
            const app = getAppForTeam(team);
            return (
              <div key={team.id} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="px-5 py-4 text-white flex items-center justify-between cursor-pointer" style={{ background: team.color || '#3B82F6' }} onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                  <div>
                    <h3 className="font-semibold text-lg">{team.name}</h3>
                    <span className="text-sm text-white/80">{team.application_name || 'No application'} · {members.length} members</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isReadOnly && (
                      <button onClick={(e) => { e.stopPropagation(); startEdit(team); }} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg text-sm">Edit</button>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-5">
                    {/* Application Info (read-only) */}
                    {app && (
                      <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 mb-4 border dark:border-slate-700">
                        <div className="grid grid-cols-5 gap-3 text-sm">
                          <div><span className="text-xs text-slate-400 block">Business Owner</span><span className="font-medium dark:text-white">{app.owner_name || '-'}</span></div>
                          <div><span className="text-xs text-slate-400 block">Technical Owner</span><span className="font-medium dark:text-white">{app.technical_owner_name || '-'}</span></div>
                          <div><span className="text-xs text-slate-400 block">L1 Team Lead</span><span className="font-medium dark:text-white">{app.app_team_lead_name || '-'}</span></div>
                          <div><span className="text-xs text-slate-400 block">L2 App Manager</span><span className="font-medium dark:text-white">{app.app_manager_name || '-'}</span></div>
                          <div><span className="text-xs text-slate-400 block">L3 App Head</span><span className="font-medium dark:text-white">{app.app_head_name || '-'}</span></div>
                        </div>
                        <div className="text-xs text-slate-400 mt-2">Approval: {app.approval_levels === 0 ? 'None' : `${app.approval_levels} Levels`} · DL: {team.distribution_email || 'Not set'}</div>
                      </div>
                    )}

                    {/* Members */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">IT Support Members ({members.length})</span>
                        {!isReadOnly && members.length >= 2 && (
                          <button onClick={async () => {
                            try {
                              await teamAPI.patch(team.id, { round_robin_enabled: !team.round_robin_enabled });
                              toast.success(team.round_robin_enabled ? 'Round-robin disabled' : 'Round-robin enabled');
                              fetch();
                            } catch { toast.error('Failed'); }
                          }}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                              team.round_robin_enabled
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                            }`}>
                            <div className={`w-7 h-4 rounded-full relative ${team.round_robin_enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                              <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${team.round_robin_enabled ? 'left-3.5' : 'left-0.5'}`} />
                            </div>
                            Round Robin {team.round_robin_enabled ? 'ON' : 'OFF'}
                          </button>
                        )}
                        {isReadOnly && members.length >= 2 && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${team.round_robin_enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                            Round Robin {team.round_robin_enabled ? 'ON' : 'OFF'}
                          </span>
                        )}
                      </div>
                      {!isReadOnly && (
                        <button onClick={() => {
                          if (addMemberTeam === team.id) { setAddMemberTeam(null); return; }
                          setAddMemberTeam(team.id);
                          setSelectedMembers(new Set());
                          setAddRole('member');
                          setMemberRoleFilter('it_support');
                          setMemberSearch('');
                          loadMemberUsers('it_support', '', 1);
                        }}
                          className="text-xs bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-sky-100">
                          <UserPlus className="w-3.5 h-3.5" /> Add Members
                        </button>
                      )}
                    </div>

                    {addMemberTeam === team.id && (
                      <div className="bg-slate-50 dark:bg-slate-900 border dark:border-slate-700 rounded-xl p-4 mb-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Add Team Members</div>
                          {selectedMembers.size > 0 && (
                            <span className="bg-sky-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{selectedMembers.size} selected</span>
                          )}
                        </div>
                        {/* Filters */}
                        <div className="flex gap-2">
                          <select value={memberRoleFilter}
                            onChange={e => { setMemberRoleFilter(e.target.value); loadMemberUsers(e.target.value, memberSearch, 1); }}
                            className="border dark:border-slate-700 rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-white flex-1">
                            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <form onSubmit={e => { e.preventDefault(); loadMemberUsers(memberRoleFilter, memberSearch, 1); }} className="flex gap-1 flex-1">
                            <div className="relative flex-1">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                              <input type="text" value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                                placeholder="Search name / email..."
                                className="w-full pl-8 pr-3 py-2 border dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                            </div>
                            <button type="submit" className="bg-slate-200 dark:bg-slate-700 px-2 py-2 rounded-lg text-xs dark:text-white">Go</button>
                          </form>
                        </div>
                        {/* User list with checkboxes */}
                        <div className="max-h-52 overflow-y-auto border dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 divide-y dark:divide-slate-700">
                          {memberLoading ? (
                            <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-sky-500" /></div>
                          ) : memberUserList.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-4">No users found — try a different filter</p>
                          ) : (
                            <>
                              {/* Select All row */}
                              {memberUserList.filter(u => !(team.members || []).some(m => m.user === u.id)).length > 0 && (
                                <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-900 text-xs font-medium text-slate-500">
                                  <input type="checkbox"
                                    checked={memberUserList.filter(u => !(team.members||[]).some(m=>m.user===u.id)).every(u => selectedMembers.has(u.id))}
                                    onChange={e => {
                                      const eligible = memberUserList.filter(u => !(team.members||[]).some(m=>m.user===u.id));
                                      if (e.target.checked) setSelectedMembers(new Set([...selectedMembers, ...eligible.map(u=>u.id)]));
                                      else setSelectedMembers(prev => { const n=new Set(prev); eligible.forEach(u=>n.delete(u.id)); return n; });
                                    }}
                                    className="rounded" />
                                  Select all on this page
                                </div>
                              )}
                              {memberUserList.map(u => {
                                const alreadyMember = (team.members || []).some(m => m.user === u.id);
                                const isChecked = selectedMembers.has(u.id);
                                return (
                                  <div key={u.id}
                                    onClick={() => {
                                      if (alreadyMember) return;
                                      setSelectedMembers(prev => {
                                        const n = new Set(prev);
                                        n.has(u.id) ? n.delete(u.id) : n.add(u.id);
                                        return n;
                                      });
                                    }}
                                    className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors
                                      ${isChecked ? 'bg-sky-50 dark:bg-sky-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}
                                      ${alreadyMember ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                    <input type="checkbox" checked={isChecked} readOnly disabled={alreadyMember}
                                      className="rounded pointer-events-none" />
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium dark:text-white">{u.full_name || u.username}</span>
                                      <span className="text-slate-400 text-xs ml-2 truncate">{u.email}</span>
                                      {alreadyMember && <span className="ml-2 text-xs text-slate-400 italic">already a member</span>}
                                    </div>
                                    <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full flex-shrink-0">
                                      {(u.role_name || '').replace(/_/g, ' ')}
                                    </span>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                        {/* Role + Add button */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">Role:</span>
                          <select value={addRole} onChange={e => setAddRole(e.target.value)}
                            className="border dark:border-slate-700 rounded-lg px-2 py-1.5 text-sm dark:bg-slate-800 dark:text-white">
                            <option value="member">Member</option>
                            <option value="senior">Senior</option>
                          </select>
                          <button onClick={() => handleAddMember(team.id)}
                            disabled={selectedMembers.size === 0}
                            className="bg-sky-500 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-sky-600 disabled:opacity-40 font-medium ml-auto">
                            Add {selectedMembers.size > 0 ? `${selectedMembers.size} Member${selectedMembers.size > 1 ? 's' : ''}` : 'Members'}
                          </button>
                          <button onClick={() => { setAddMemberTeam(null); setSelectedMembers(new Set()); }}
                            className="text-slate-400 hover:text-slate-600 p-1"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    )}

                    {members.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">No members yet. Add IT support users who will handle tickets.</p>
                    ) : (
                      <div className="space-y-2">
                        {members.map(m => (
                          <div key={m.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2.5">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${m.is_available ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                              <span className="text-sm font-medium dark:text-white">{m.user_name}</span>
                              <span className="text-xs text-slate-500">{m.user_email}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.role_in_team === 'senior' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>{m.role_in_team}</span>
                            </div>
                            {!isReadOnly && (
                              <button onClick={() => handleRemoveMember(team.id, m.user)} className="text-slate-400 hover:text-rose-500 p-1"><UserMinus className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
